<template>
  <section>
    <h2 class="text-xl md:text-2xl font-bold mb-4 md:mb-6">长篇小说创作</h2>

    <div class="flex mb-6 border-b border-gray-200">
      <button
        class="px-4 md:px-6 py-2 rounded-t-md"
        :class="{ 'tab-active': inputMode === 'text' }"
        @click="handleChangeInputMode('text')"
      >
        输入文本
      </button>
      <button
        class="px-4 md:px-6 py-2 rounded-t-md"
        :class="{ 'tab-active': inputMode === 'file' }"
        @click="handleChangeInputMode('file')"
      >
        上传文件
      </button>
    </div>

    <div v-show="inputMode === 'text'" class="mb-6">
      <textarea v-model="content" class="w-full bg-white rounded-md p-3 resize-y min-h-[200px] focus:outline-none focus:ring-1 focus:ring-primary border border-gray-200" placeholder="请输入文本内容（最多30000字符）..."></textarea>
      <p class="text-textSub text-sm mt-2">当前字符数：{{ content.length }}/30000</p>
    </div>

    <div v-show="inputMode === 'file'" class="mb-6">
      <div class="w-full bg-white rounded-md p-8 flex flex-col items-center justify-center min-h-[200px] border-2 border-dashed border-gray-200 hover:border-primary transition-colors">
        <i class="fa-solid fa-upload text-4xl text-textSub mb-4"></i>
        <p class="text-textSub mb-4">点击或拖拽文件至此处上传</p>
        <p class="text-xs text-textSub">支持格式：TXT（最大30000字符）</p>
        <input ref="fileInput" type="file" class="hidden" accept=".txt" @change="onFileChange">
        <button class="mt-4 bg-primary text-white px-4 py-2 rounded-md btn-hover" @click="$refs.fileInput.click()">选择文件</button>
      </div>
      <div v-if="selectedFile" class="mt-3 bg-gray-50 border border-gray-200 rounded-md px-3 py-2 flex items-center justify-between text-xs md:text-sm text-textSub">
        <div class="flex items-center gap-2">
          <i class="fa-solid fa-file-lines text-textSub"></i>
          <span class="break-all">{{ selectedFile.name }}</span>
        </div>
        <button
          type="button"
          class="text-primary hover:underline ml-3 whitespace-nowrap"
          @click="clearSelectedFile"
        >
          删除并重新选择
        </button>
      </div>
    </div>

    <div v-show="!resultVisible" class="mb-6">
      <button
        type="button"
        class="w-full bg-primary text-white py-3 rounded-md text-lg btn-hover inline-flex items-center justify-center gap-2"
        @click="startCreate"
      >
        <span>开始创作</span>
        <i class="fa-solid fa-bolt shrink-0 opacity-95" aria-hidden="true"></i>
      </button>
    </div>
    <div v-show="resultVisible" class="flex gap-4 mb-6">
      <button
        class="flex-1 bg-primary text-white py-3 rounded-md text-lg btn-hover disabled:opacity-60 disabled:cursor-not-allowed"
        type="button"
        :disabled="signSubmitting || pushSubmitting"
        @click="handleSign"
      >
        {{ signSubmitting ? '提交中...' : '一键签约' }}
      </button>
      <button
        class="flex-1 bg-white border border-primary text-primary py-3 rounded-md text-lg btn-hover disabled:opacity-60 disabled:cursor-not-allowed"
        type="button"
        :disabled="signSubmitting || pushSubmitting"
        @click="handlePush"
      >
        {{ pushSubmitting ? '发布中...' : '发布到简书' }}
      </button>
    </div>

    <div v-show="resultVisible" class="bg-white rounded-md p-6 border border-gray-200">
      <div class="flex justify-between items-center mb-4">
        <h3 class="text-xl font-semibold">创作结果</h3>
        <button class="copy-btn" @click="$emit('copy', longNovelContent)">
          <i class="fa-solid fa-copy mr-1"></i>一键复制
        </button>
      </div>
      <div class="mb-4">
        <h4 class="font-medium mb-2">导语推荐：</h4>
        <p v-if="!introList" class="text-textSub mb-4">暂无导语推荐，请查看下方小说内容。</p>
        <div v-else class="text-textSub whitespace-pre-line mb-4">{{ introList }}</div>
      </div>
      <div>
        <h4 class="font-medium mb-2">小说内容：</h4>
        <div class="text-textSub whitespace-pre-line mb-4">{{ longNovelContent }}</div>
        <div class="flex justify-end">
          <button class="copy-btn" @click="$emit('copy', longNovelContent)">
            <i class="fa-solid fa-copy mr-1"></i>一键复制
          </button>
        </div>
      </div>
      <div class="flex justify-center mt-6">
        <button class="bg-gray-200 text-textMain px-6 py-2 rounded-md btn-hover" @click="clearResult">
          <i class="fa-solid fa-trash mr-1"></i>清除结果并重置
        </button>
      </div>
    </div>

    <!-- 一键签约：填写作品名称 -->
    <div
      v-if="signModalVisible"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      @click.self="closeSignModal"
    >
      <div class="bg-white rounded-lg p-6 w-full max-w-md border border-gray-200 shadow-lg mx-4">
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-xl font-semibold">一键签约</h3>
          <button class="text-textSub text-xl cursor-pointer" @click="closeSignModal">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>
        <div class="mb-5">
          <label class="block text-sm text-textSub mb-1">作品名称</label>
          <input
            v-model="signWorkName"
            type="text"
            class="w-full border border-gray-200 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-primary"
            placeholder="请输入作品名称"
          >
        </div>
        <div class="flex gap-3">
          <button
            class="flex-1 border border-gray-300 text-textMain py-2 rounded-md btn-hover"
            type="button"
            @click="closeSignModal"
          >
            取消
          </button>
          <button
            class="flex-1 bg-primary text-white py-2 rounded-md btn-hover"
            type="button"
            :disabled="signSubmitting"
            @click="submitSign"
          >
            {{ signSubmitting ? '提交中...' : '确认签约' }}
          </button>
        </div>
      </div>
    </div>

    <div class="guide-module">
      <h3 class="guide-title">
        <i class="fa-solid fa-lightbulb guide-icon"></i>
        长篇小说创作使用攻略
      </h3>
      <div class="guide-list">
        <div v-for="(item, index) in guideList" :key="index" class="guide-item">
          <i class="fa-solid fa-check-circle guide-icon"></i>
          <span>{{ item }}</span>
        </div>
      </div>
    </div>

    <!-- 创作中浮层提示 -->
    <div
      v-if="creating"
      class="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50"
    >
      <div class="bg-white rounded-lg px-6 py-4 shadow-md border border-gray-200 text-center max-w-xs">
        <div class="mb-2 text-primary text-lg font-semibold">正在为你创作长篇小说...</div>
        <p class="text-textSub text-sm">预计需要数分钟，请稍候，不要关闭或刷新页面。</p>
      </div>
    </div>
  </section>
</template>

<script>
import { createLongNovelByText, uploadLongNovelFile, queryLongNovelTask, signLongNovel, pushLongNovel } from '../api/longNovel'
import { poll } from '../utils/poll'
import { readTextFileWithLimit } from '../utils/uploadValidator'

var DEFAULT_GUIDE = [
  '输入文本时明确故事类型和核心人物，生成效果更佳',
  '上传文件建议分段整理，避免大段无结构文本',
  '梗概模式适合快速生成故事框架，原文模式适合细节创作',
  '长篇小说建议分章节生成，再整合完整内容',
  '生成结果可多次调整关键词，直到符合预期风格',
  '签约长篇小说需确保内容符合平台连载规范'
]

export default {
  name: 'LongNovel',
  data () {
    return {
      inputMode: 'text',
      content: '',
      resultVisible: false,
      longNovelContent: '',
      introList: '',
      guideList: DEFAULT_GUIDE.slice(),
      creating: false,
      selectedFile: null,
      signModalVisible: false,
      signWorkName: '',
      signSubmitting: false,
      pushSubmitting: false,
      taskId: ''
    }
  },
  methods: {
    handleChangeInputMode (mode) {
      if (this.resultVisible) {
        this.$emit(
          'show-modal',
          '提示',
          '当前已有创作结果，如需重新创作，请先点击下方"清除结果并重置"，再切换创作类型或输入方式。'
        )
        return
      }
      this.inputMode = mode
    },
    handleSign () {
      if (this.signSubmitting || this.pushSubmitting) return
      const content = this.longNovelContent
      if (!content || !content.trim()) {
        this.$emit('show-modal', '签约失败', '当前没有可用于签约的创作内容，请先完成创作。')
        return
      }
      this.signModalVisible = true
    },
    closeSignModal () {
      this.signModalVisible = false
      this.signWorkName = ''
      this.signSubmitting = false
    },
    async submitSign () {
      if (this.signSubmitting || this.pushSubmitting) return
      const content = this.longNovelContent
      if (!content || !content.trim()) {
        this.$emit('show-modal', '签约失败', '当前没有可用于签约的创作内容，请先完成创作。')
        return
      }
      const workName = (this.signWorkName || '').trim()
      if (!workName) {
        this.$emit('show-modal', '提示', '请输入作品名称。')
        return
      }
      this.signSubmitting = true
      try {
        await signLongNovel({ content, workName })
        this.signSubmitting = false
        this.signModalVisible = false
        this.$emit('show-modal', '签约提示', '签约申请已提交，请等待平台审核！')
        this.clearResult()
      } catch (err) {
        this.signSubmitting = false
        const msg = (err && err.message) || '签约请求失败，请稍后重试。'
        this.$emit('show-modal', '签约失败', msg)
      }
    },
    async handlePush () {
      if (this.pushSubmitting || this.signSubmitting) return
      const taskId = this.taskId
      if (!taskId) {
        this.$emit('show-modal', '发布失败', '请先完成创作后再发布到简书。')
        return
      }
      this.pushSubmitting = true
      try {
        await pushLongNovel({ taskId })
        this.$emit('show-modal', '发布提示', '作品已成功发布到简书平台！')
      } catch (err) {
        const msg = (err && err.message) || '发布请求失败，请稍后重试。'
        this.$emit('show-modal', '发布失败', msg)
      } finally {
        this.pushSubmitting = false
      }
    },
    async startCreate () {
      if (this.creating) return

      if (this.inputMode === 'text') {
        const content = this.content || ''
        if (!content.trim()) {
          this.$emit('show-modal', '提示', '请输入要创作的文本内容。')
          return
        }
        this.creating = true
        try {
          const res = await createLongNovelByText({ content })
          const taskId = (res && (res.taskId != null ? res.taskId : (res.data && res.data.taskId))) || ''
          if (!taskId) {
            this.creating = false
            this.$emit('show-modal', '创作失败', '未返回任务ID，请稍后重试。')
            return
          }
          this.taskId = String(taskId)
          const data = await this.pollTaskResult(taskId)
          if (data) {
            this.longNovelContent = data.text || '创作结果为空，请稍后重试或调整输入内容。'
            this.introList = data.introList || '创作结果为空，请稍后重试或调整输入内容。'
            this.resultVisible = true
          }
        } catch (err) {
          const msg = (err && err.message) || '文章设定出现问题，请修改后重新提交。'
          this.$emit('show-modal', '创作失败', msg)
        } finally {
          this.creating = false
        }
      } else if (this.inputMode === 'file') {
        if (!this.selectedFile) {
          this.$emit('show-modal', '提示', '请先选择要上传的文件。')
          return
        }
        const formData = new FormData()
        formData.append('inputMode', 'file')
        formData.append('file', this.selectedFile)
        this.creating = true
        try {
          const res = await uploadLongNovelFile(formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          })
          const taskId = (res && (res.taskId != null ? res.taskId : (res.data && res.data.taskId))) || ''
          if (!taskId) {
            this.creating = false
            this.$emit('show-modal', '创作失败', '未返回任务ID，请稍后重试。')
            return
          }
          this.taskId = String(taskId)
          const data = await this.pollTaskResult(taskId)
          if (data) {
            let txt = ''
            let introStr = ''
            if (data && typeof data.content === 'string') {
              txt = data.content
            } else if (data && typeof data.text === 'string') {
              txt = data.text
            } else if (typeof data === 'string') {
              txt = data
            }
            if (data) {
              introStr = (data.introList != null ? String(data.introList) : (data.leadList != null ? String(data.leadList) : '')) || ''
            }
            this.longNovelContent = txt || '创作结果为空，请稍后重试或更换文件。'
            this.introList = introStr
            this.resultVisible = true
          }
        } catch (err) {
          const msg = (err && err.message) || '文件上传或处理失败，请稍后重试。'
          this.$emit('show-modal', '创作失败', msg)
        } finally {
          this.creating = false
        }
      }
    },
    pollTaskResult (taskId) {
      return poll(function () {
        return queryLongNovelTask(taskId)
      }, {
        shouldStop: function (res) {
          return res && res.taskStatus === 'SUCCESS'
        },
        isError: function (res) {
          if (res && res.taskStatus === 'FAILED') {
            const msg = (res && (res.message || res.msg)) || '任务执行失败'
            return new Error(msg)
          }
          return null
        },
        interval: 15000,
        maxTimes: 150
      })
    },
    clearResult () {
      this.content = ''
      this.resultVisible = false
      this.inputMode = 'text'
      this.longNovelContent = ''
      this.introList = ''
      this.selectedFile = null
      this.taskId = ''
      this.signModalVisible = false
      this.signWorkName = ''
      this.signSubmitting = false
      this.pushSubmitting = false
    },
    async onFileChange (e) {
      const files = e && e.target && e.target.files
      if (!files || !files[0]) return
      const file = files[0]

      try {
        const info = await readTextFileWithLimit(file, { maxLength: 30000 })
        this.selectedFile = file
        this.$emit('show-modal', '文件选择成功', '已选择文件：' + file.name + '\n当前字数：' + info.length + '（上限 30000 字）')
      } catch (err) {
        this.selectedFile = null
        if (this.$refs.fileInput && this.$refs.fileInput.value) {
          this.$refs.fileInput.value = ''
        }
        const msg = err && err.message ? err.message : '文件读取失败，请稍后重试或更换文件。'
        this.$emit('show-modal', err && err.code === 'FILE_TOO_LARGE' ? '文件过大' : '文件读取失败', msg)
      }
    },
    clearSelectedFile () {
      this.selectedFile = null
      if (this.$refs.fileInput && this.$refs.fileInput.value) {
        this.$refs.fileInput.value = ''
      }
    }
  }
}
</script>
